import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

type TimeSpinnerProps = {
  isLoading: boolean;
  className?: string;
  seconds?: number;
};

const TimeSpinner = ({ isLoading, className, seconds }: TimeSpinnerProps) => {
  const totalTime = seconds ?? 100;
  const [countDownTime, setCountDownTime] = useState(totalTime);
  const [showTimer, setShowTimer] = useState(false);

  const countInterval = useRef<NodeJS.Timeout | null>(null);
  const timeout = useRef<NodeJS.Timeout | null>(null);
  const secondCircle = useRef(null);

  const startCountDown = useCallback(() => {
    if (countInterval.current !== null) {
      clearInterval(countInterval.current);
    }

    countInterval.current = setInterval(() => {
      setCountDownTime((prev) => (prev === 0 ? 0 : prev - 1));
    }, 1000);
  }, []);

  useEffect(() => {
    if (countDownTime < 1) {
      if (countInterval?.current !== null) {
        clearInterval(countInterval.current);
      }
    }
    if (secondCircle?.current !== null) {
      (secondCircle.current as never as SVGElement).style.strokeDashoffset = `${
        (countDownTime * 451) / totalTime
      }px`;
    }
  }, [secondCircle, countInterval, countDownTime, totalTime]);

  useEffect(() => {
    if (isLoading) {
      setShowTimer(true);
      setCountDownTime(totalTime);
      startCountDown();
    } else {
      setCountDownTime(0);
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
      timeout.current = setTimeout(() => {
        setShowTimer(false);
      }, 1500);
    }

    return () => {
      timeout.current !== null && clearTimeout(timeout.current);
    };
  }, [isLoading, startCountDown, totalTime]);

  if (!showTimer) {
    return null;
  }

  return (
    <div className="absolute inset-0 z-[100] flex max-h-screen items-center justify-center bg-primary-lighter/40">
      <div className={cn('relative', className)}>
        <svg className="h-48 w-48 -rotate-90 scale-[0.6] sm:scale-[0.8] lg:scale-[1]">
          <circle
            r="70"
            cx="90"
            cy="90"
            className="fill-transparent stroke-text-secondary stroke-[12px]"
          ></circle>
          <circle
            ref={secondCircle}
            r="70"
            cx="90"
            cy="90"
            className="fill-transparent stroke-primary-light stroke-[12px]"
            style={{
              strokeDasharray: '451px',
            }}
          ></circle>
        </svg>
        <div className="absolute left-11 top-16 flex h-20 w-24 flex-col items-center justify-center text-xl font-semibold lg:text-2xl">
          <span className="text-center">{100 - countDownTime}%</span>
        </div>
      </div>
    </div>
  );
};

export default TimeSpinner;
